import { useCallback, useSyncExternalStore } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { QueryIntent } from '@atlassian/search-ai/constants';
import { type IntentClassificationData } from '@atlassian/search-ai/query-intent-api/types';
import { isNavContentIntent, isNavigationalIntent } from '@atlassian/search-ai/utils';

interface SmartAnswersIntentStore {
	workspaceId: string | null;
	intentClassificationData: IntentClassificationData | null | undefined;
	sainJql: string | null;
	queryIntent: QueryIntent;
	/** True once `useQueryIntent` has finished classifying the current query. */
	isIntentClassified: boolean;
	/** True when SAIN will render an AI answer (not just a bookmark/popular suggestion/nothing). */
	isSainAnswerVisible: boolean;
	/**
	 * True when SAIN's answer card is currently expanded (default after answer
	 * is ready). False when the user has collapsed it via the SAIN footer
	 * affordance, or while SAIN has not rendered yet.
	 *
	 * Consumers should treat the combination of `isSainAnswerVisible` +
	 * `isSainAnswerExpanded` as the source of truth for the FPS Rovo Chat Box
	 * visibility rules (see Confluence: "FPS Rovo Chat Box Visibility Rules").
	 */
	isSainAnswerExpanded: boolean;
	/**
	 * The query that SAIN is currently answering (or last answered). Mirrors
	 * `commonAttributes.query` from `AIAnswerContext`. Empty string when SAIN
	 * has not produced an answer yet.
	 */
	sainQuery: string;
	/**
	 * The plain-text answer SAIN produced for `sainQuery`. Mirrors
	 * `commonAttributes.answerString` from `AIAnswerContext`. Empty string
	 * while SAIN is still streaming, has not rendered, or produced no
	 * answer.
	 */
	sainAnswerString: string;
}

const NAV_BOOST_SCORE_THRESHOLD = 0.5;

const initialState: SmartAnswersIntentStore = {
	workspaceId: null,
	intentClassificationData: null,
	sainJql: null,
	queryIntent: QueryIntent.DEFAULT,
	isIntentClassified: false,
	isSainAnswerVisible: false,
	isSainAnswerExpanded: false,
	sainQuery: '',
	sainAnswerString: '',
};

// Singleton state shared across all components
const sharedState: { current: SmartAnswersIntentStore } = {
	current: { ...initialState },
};

// Pub/sub listeners for reactive SAIN JQL updates
type Listener = () => void;
const sainJqlListeners = new Set<Listener>();

function notifySainJqlListeners(): void {
	sainJqlListeners.forEach((l) => l());
}

function subscribeSainJql(listener: Listener): () => void {
	sainJqlListeners.add(listener);
	return () => {
		sainJqlListeners.delete(listener);
	};
}

function getSainJqlSnapshot(): string | null {
	return sharedState.current.sainJql;
}

// Pub/sub listeners for reactive queryIntent updates
const queryIntentListeners = new Set<Listener>();

function notifyQueryIntentListeners(): void {
	queryIntentListeners.forEach((l) => l());
}

function subscribeQueryIntent(listener: Listener): () => void {
	queryIntentListeners.add(listener);
	return () => {
		queryIntentListeners.delete(listener);
	};
}

function getQueryIntentSnapshot(): QueryIntent {
	return sharedState.current.queryIntent;
}

// Pub/sub listeners for reactive isIntentClassified updates
const isIntentClassifiedListeners = new Set<Listener>();

function notifyIsIntentClassifiedListeners(): void {
	isIntentClassifiedListeners.forEach((l) => l());
}

function subscribeIsIntentClassified(listener: Listener): () => void {
	isIntentClassifiedListeners.add(listener);
	return () => {
		isIntentClassifiedListeners.delete(listener);
	};
}

function getIsIntentClassifiedSnapshot(): boolean {
	return sharedState.current.isIntentClassified;
}

// Pub/sub listeners for reactive isSainAnswerVisible updates
const isSainAnswerVisibleListeners = new Set<Listener>();

function notifyIsSainAnswerVisibleListeners(): void {
	isSainAnswerVisibleListeners.forEach((l) => l());
}

function subscribeIsSainAnswerVisible(listener: Listener): () => void {
	isSainAnswerVisibleListeners.add(listener);
	return () => {
		isSainAnswerVisibleListeners.delete(listener);
	};
}

function getIsSainAnswerVisibleSnapshot(): boolean {
	return sharedState.current.isSainAnswerVisible;
}

// Pub/sub listeners for reactive isSainAnswerExpanded updates
const isSainAnswerExpandedListeners = new Set<Listener>();

function notifyIsSainAnswerExpandedListeners(): void {
	isSainAnswerExpandedListeners.forEach((l) => l());
}

function subscribeIsSainAnswerExpanded(listener: Listener): () => void {
	isSainAnswerExpandedListeners.add(listener);
	return () => {
		isSainAnswerExpandedListeners.delete(listener);
	};
}

function getIsSainAnswerExpandedSnapshot(): boolean {
	return sharedState.current.isSainAnswerExpanded;
}

// Pub/sub listeners for reactive sainQuery / sainAnswerString updates.
// A single listener bucket is used because both fields are produced by the
// same source (SAIN's AIAnswerContext) and are always read together by
// consumers that want to seed Rovo Chat with the prior SAIN turn.
const sainAnswerListeners = new Set<Listener>();

function notifySainAnswerListeners(): void {
	sainAnswerListeners.forEach((l) => l());
}

function subscribeSainAnswer(listener: Listener): () => void {
	sainAnswerListeners.add(listener);
	return () => {
		sainAnswerListeners.delete(listener);
	};
}

function getSainQuerySnapshot(): string {
	return sharedState.current.sainQuery;
}

function getSainAnswerStringSnapshot(): string {
	return sharedState.current.sainAnswerString;
}

/**
 * Reactive hook that subscribes to queryIntent changes in the shared store.
 * Allows multiple components to read the same queryIntent without each
 * calling useQueryIntent() independently (which would trigger duplicate
 * API calls and analytics events).
 */
export function useSharedQueryIntent(): QueryIntent {
	return useSyncExternalStore(subscribeQueryIntent, getQueryIntentSnapshot, getQueryIntentSnapshot);
}

/** Reactive hook reading `isIntentClassified` from the shared store. */
export function useSharedIsIntentClassified(): boolean {
	return useSyncExternalStore(
		subscribeIsIntentClassified,
		getIsIntentClassifiedSnapshot,
		getIsIntentClassifiedSnapshot,
	);
}

/** Reactive hook reading `isSainAnswerVisible` from the shared store. */
export function useSharedIsSainAnswerVisible(): boolean {
	return useSyncExternalStore(
		subscribeIsSainAnswerVisible,
		getIsSainAnswerVisibleSnapshot,
		getIsSainAnswerVisibleSnapshot,
	);
}

/**
 * Reactive hook reading `isSainAnswerExpanded` from the shared store.
 *
 * Combined with {@link useSharedIsSainAnswerVisible}, this implements the
 * three SAIN states defined in the "FPS Rovo Chat Box Visibility Rules"
 * Confluence doc:
 *
 * - Not present : `isSainAnswerVisible === false`
 * - Present + collapsed: `isSainAnswerVisible === true && isSainAnswerExpanded === false`
 * - Present + expanded : `isSainAnswerVisible === true && isSainAnswerExpanded === true`
 */
export function useSharedIsSainAnswerExpanded(): boolean {
	return useSyncExternalStore(
		subscribeIsSainAnswerExpanded,
		getIsSainAnswerExpandedSnapshot,
		getIsSainAnswerExpandedSnapshot,
	);
}

/**
 * Reactive hook reading the latest SAIN answer query string.
 *
 * Returns an empty string when SAIN has not produced an answer yet (or there
 * is no SAIN render). Callers that want to seed Rovo Chat with the prior SAIN
 * turn should additionally check {@link useSharedIsSainAnswerExpanded} per the
 * "FPS Rovo Chat Box Visibility Rules" doc, and read the answer body via
 * {@link useSharedSainAnswerString}.
 *
 * The query and answer body are exposed as two separate primitive hooks (rather
 * than a single object) so each returns a referentially stable value without
 * needing a cached snapshot.
 */
export function useSharedSainQuery(): string {
	return useSyncExternalStore(subscribeSainAnswer, getSainQuerySnapshot, getSainQuerySnapshot);
}

/**
 * Reactive hook reading the latest SAIN answer body string.
 *
 * Returns an empty string when SAIN has not produced an answer yet. See
 * {@link useSharedSainQuery} for the companion query hook.
 */
export function useSharedSainAnswerString(): string {
	return useSyncExternalStore(
		subscribeSainAnswer,
		getSainAnswerStringSnapshot,
		getSainAnswerStringSnapshot,
	);
}

/**
 * Reactive hook that subscribes to SAIN JQL changes.
 * Unlike getSainJql() which is read-once, this triggers re-renders
 * whenever setSainJql() is called, making it safe to use in href computation.
 */
export function useSainJql(): string | null {
	return useSyncExternalStore(subscribeSainJql, getSainJqlSnapshot, getSainJqlSnapshot);
}

/**
 * React hook for managing smart answers intent store
 * Provides state management for smart answers high confidence result cards
 * Uses a shared singleton state to ensure all components see the same results
 */
export const useSmartAnswersIntentStore: () => {
	setWorkspaceId: (workspaceId: string) => void;
	getWorkspaceId: () => string | null;
	setQueryIntentClassificationData: (intentClassificationData: IntentClassificationData) => void;
	isSAINNavigationalExperienceEligible: ({
		position,
		url,
		navBoostScore,
	}: {
		position: number;
		url: string;
		navBoostScore: number | null | undefined;
	}) => boolean;
	clearStore: () => void;
	setSainJql: (jql: string | null) => void;
	getSainJql: () => string | null;
	setQueryIntent: (queryIntent: QueryIntent) => void;
	getQueryIntent: () => QueryIntent;
	setIsIntentClassified: (isIntentClassified: boolean) => void;
	setIsSainAnswerVisible: (isSainAnswerVisible: boolean) => void;
	setIsSainAnswerExpanded: (isSainAnswerExpanded: boolean) => void;
	setSainAnswer: (payload: { query: string; answerString: string }) => void;
} = () => {
	const state = sharedState;

	const setWorkspaceId = useCallback(
		(workspaceId: string): void => {
			state.current.workspaceId = workspaceId;
		},
		[state],
	);

	const getWorkspaceId = useCallback((): string | null => {
		return state.current.workspaceId;
	}, [state]);

	const setQueryIntentClassificationData = useCallback(
		(intentClassificationData: IntentClassificationData): void => {
			state.current.intentClassificationData = intentClassificationData;
		},
		[state],
	);

	const clearStore = useCallback((): void => {
		state.current = { ...initialState };
		notifySainJqlListeners();
		notifyQueryIntentListeners();
		notifyIsIntentClassifiedListeners();
		notifyIsSainAnswerVisibleListeners();
		notifyIsSainAnswerExpandedListeners();
		notifySainAnswerListeners();
	}, [state]);

	const setQueryIntent = useCallback(
		(queryIntent: QueryIntent): void => {
			if (state.current.queryIntent === queryIntent) {
				return;
			}
			state.current.queryIntent = queryIntent;
			notifyQueryIntentListeners();
		},
		[state],
	);

	const setIsIntentClassified = useCallback(
		(isIntentClassified: boolean): void => {
			if (state.current.isIntentClassified === isIntentClassified) {
				return;
			}
			state.current.isIntentClassified = isIntentClassified;
			notifyIsIntentClassifiedListeners();
		},
		[state],
	);

	const setIsSainAnswerVisible = useCallback(
		(isSainAnswerVisible: boolean): void => {
			if (state.current.isSainAnswerVisible === isSainAnswerVisible) {
				return;
			}
			state.current.isSainAnswerVisible = isSainAnswerVisible;
			notifyIsSainAnswerVisibleListeners();
		},
		[state],
	);

	const setIsSainAnswerExpanded = useCallback(
		(isSainAnswerExpanded: boolean): void => {
			if (state.current.isSainAnswerExpanded === isSainAnswerExpanded) {
				return;
			}
			state.current.isSainAnswerExpanded = isSainAnswerExpanded;
			notifyIsSainAnswerExpandedListeners();
		},
		[state],
	);

	const setSainAnswer = useCallback(
		({ query, answerString }: { query: string; answerString: string }): void => {
			if (state.current.sainQuery === query && state.current.sainAnswerString === answerString) {
				return;
			}
			state.current.sainQuery = query;
			state.current.sainAnswerString = answerString;
			notifySainAnswerListeners();
		},
		[state],
	);

	const getQueryIntent = useCallback((): QueryIntent => {
		return state.current.queryIntent;
	}, [state]);

	const setSainJql = useCallback(
		(jql: string | null): void => {
			state.current.sainJql = jql;
			notifySainJqlListeners();
		},
		[state],
	);

	const getSainJql = useCallback((): string | null => {
		return state.current.sainJql;
	}, [state]);

	const isSAINNavigationalExperienceEligible = useCallback(
		({
			position,
			url,
			navBoostScore,
		}: {
			position: number;
			url: string;
			navBoostScore: number | null | undefined;
		}): boolean => {
			if (position === 0 && fg('kd_enable_sain_hero_nav')) {
				const queryIntentClassificationData = state.current.intentClassificationData;

				// use the URL check for nav content intent
				if (queryIntentClassificationData && isNavContentIntent(queryIntentClassificationData)) {
					const navContentUrl = queryIntentClassificationData.url;
					if (navContentUrl === url) {
						return true;
					}
				}

				// use the nav boost score check for navigational intent or nav content intent if we missed the document
				// because the URL returned is not for the first result
				if (
					queryIntentClassificationData &&
					(isNavigationalIntent(queryIntentClassificationData) ||
						isNavContentIntent(queryIntentClassificationData))
				) {
					if (navBoostScore && navBoostScore > NAV_BOOST_SCORE_THRESHOLD) {
						return true;
					}
				}
			}

			return false;
		},
		[state],
	);

	return {
		setWorkspaceId,
		getWorkspaceId,
		setQueryIntentClassificationData,
		isSAINNavigationalExperienceEligible,
		clearStore,
		setSainJql,
		getSainJql,
		setQueryIntent,
		getQueryIntent,
		setIsIntentClassified,
		setIsSainAnswerVisible,
		setIsSainAnswerExpanded,
		setSainAnswer,
	};
};
